import React, { useState, useEffect, useRef } from 'react';
import LoadingScreen from './LoadingScreen';
import ProfileDropdown from './ProfileDropdown';
import { formatDate, calculateCountdown } from './utils';
import RaceStories from './RaceStories';
import api from './api';
import { MANTRA_PRESETS } from './constants/mantraPresets';
import useIsMobile from './hooks/useIsMobile';

const MyRaceDetail = ({ race, onBack, raceReadyMode, user, onLogout, onToggleRaceReady }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [mantra, setMantra] = useState('');
  const [editingMantra, setEditingMantra] = useState(false);
  const [tempMantra, setTempMantra] = useState('');
  const [showMantraPicker, setShowMantraPicker] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const [newItem, setNewItem] = useState('');
  const [travelDetails, setTravelDetails] = useState([]);
  const [showTravelModal, setShowTravelModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [previousChecklists, setPreviousChecklists] = useState([]);
  const [travelForm, setTravelForm] = useState({
    type: 'flight', confirmation: '',
    airline: '', flight_number: '', departure_airport: '', arrival_airport: '',
    departure_date: '', departure_time: '', return_date: '', return_time: '',
    company: '', pickup_location: '', pickup_date: '', pickup_time: '',
    dropoff_location: '', dropoff_date: '', dropoff_time: '',
    stay_name: '', address: '', checkin_date: '', checkout_date: '',
    description: '', start_date: '', start_time: '', end_date: '', end_time: '', notes: '',
  });
  const [editingTravelItem, setEditingTravelItem] = useState(null);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [timeGoal, setTimeGoal] = useState('');
  const [fuelPlan, setFuelPlan] = useState('');
  const [raceStrategy, setRaceStrategy] = useState('');
  const [editingStrategy, setEditingStrategy] = useState(false);
  const [tempStrategy, setTempStrategy] = useState({ timeGoal: '', fuelPlan: '', raceStrategy: '' });
  const [loading, setLoading] = useState(true);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const [newItemCategory, setNewItemCategory] = useState('Custom');
  const [nutritionItems, setNutritionItems] = useState([]);
  const [fuelBrandDirectory, setFuelBrandDirectory] = useState([]);
  const [homemadeRecipes, setHomemadeRecipes] = useState([]);
  const [showNutritionModal, setShowNutritionModal] = useState(false);
  const [editingNutritionItem, setEditingNutritionItem] = useState(null);
  const [nutritionForm, setNutritionForm] = useState({
    brand_name: '', brand_id: null, flavor: '', timing: '', notes: '',
    is_homemade: false, homemade_name: '', homemade_ingredients: '', homemade_prep_notes: '',
    save_as_recipe: false,
  });
  const [fuelSearch, setFuelSearch] = useState('');
  const [showFuelDropdown, setShowFuelDropdown] = useState(false);

  // Pace splits
  const [paceSplits, setPaceSplits] = useState({ goalTimeSeconds: 0, distanceMiles: 26.2, pacingStrategy: 'even', splits: [] });
  const [splitsGoalHours, setSplitsGoalHours] = useState(0);
  const [splitsGoalMinutes, setSplitsGoalMinutes] = useState(0);
  const [splitsGoalSeconds, setSplitsGoalSeconds] = useState(0);
  const [editingMile, setEditingMile] = useState(null);
  const [editPaceInput, setEditPaceInput] = useState('');
  const [hasCustomSplits, setHasCustomSplits] = useState(false);
  const [triathlonLegs, setTriathlonLegs] = useState({ swimSeconds: 0, t1Seconds: 0, bikeSeconds: 0, t2Seconds: 0, runSeconds: 0 });

  // Race day timeline
  const [timeline, setTimeline] = useState({ events: [], gunTime: '', notes: '' });
  const [showTimelineEventModal, setShowTimelineEventModal] = useState(false);
  const [timelineEventForm, setTimelineEventForm] = useState({ time: '', label: '', type: 'prep', notes: '' });

  // Reflection
  const [reflection, setReflection] = useState({ actualTimeSeconds: null, overallRating: null, whatWorked: '', whatDidnt: '', fuelNotes: '', weatherImpact: '', wouldChange: '', highlight: '' });
  const [reflectionHours, setReflectionHours] = useState(0);
  const [reflectionMinutes, setReflectionMinutes] = useState(0);
  const [reflectionSeconds, setReflectionSeconds] = useState(0);

  // Charity (#168)
  const [charityName, setCharityName] = useState('');
  const [fundraisingUrl, setFundraisingUrl] = useState('');
  const [editingCharity, setEditingCharity] = useState(false);

  // Save feedback
  const [saveMessage, setSaveMessage] = useState('');

  const isMobile = useIsMobile();
  const raceId = race.communityId;

  useEffect(() => {
    fetchRaceData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceId]);

  const fetchRaceData = async () => {
    try {
      const [mantraRes, checklistRes, travelRes, strategyRes, nutritionRes, directoryRes, splitsRes, timelineRes, reflectionRes, registrationsRes] = await Promise.all([
        api.get(`/mantra/${raceId}`),
        api.get(`/checklist/${raceId}`),
        api.get(`/travel/${raceId}`),
        api.get(`/strategy/${raceId}`),
        api.get(`/fuel/${raceId}`),
        api.get(`/fuel/directory`),
        api.get(`/strategy/${raceId}/splits`),
        api.get(`/strategy/${raceId}/timeline`),
        api.get(`/strategy/${raceId}/reflection`),
        api.get('/registrations'),
      ]);

      setMantra(mantraRes.data.text || '');
      setChecklist(checklistRes.data);
      setTravelDetails(travelRes.data);
      setTimeGoal(strategyRes.data.time_goal || '');
      setFuelPlan(strategyRes.data.fuel_plan || '');
      setRaceStrategy(strategyRes.data.race_strategy || '');
      setNutritionItems(nutritionRes.data);
      setFuelBrandDirectory(directoryRes.data);

      // Pace splits
      const sd = splitsRes.data;
      setPaceSplits(sd);
      if (sd.goalTimeSeconds > 0) {
        setSplitsGoalHours(Math.floor(sd.goalTimeSeconds / 3600));
        setSplitsGoalMinutes(Math.floor((sd.goalTimeSeconds % 3600) / 60));
        setSplitsGoalSeconds(sd.goalTimeSeconds % 60);
      }
      if (sd.triathlonLegs) {
        setTriathlonLegs(sd.triathlonLegs);
      }

      // Timeline
      setTimeline(timelineRes.data);

      // Charity data from registrations
      const thisReg = registrationsRes.data.find(r => r.raceId === raceId);
      if (thisReg) {
        setCharityName(thisReg.charityName || '');
        setFundraisingUrl(thisReg.fundraisingUrl || '');
      }

      // Reflection
      const rd = reflectionRes.data;
      setReflection(rd);
      if (rd.actualTimeSeconds) {
        setReflectionHours(Math.floor(rd.actualTimeSeconds / 3600));
        setReflectionMinutes(Math.floor((rd.actualTimeSeconds % 3600) / 60));
        setReflectionSeconds(rd.actualTimeSeconds % 60);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching race data:', error);
      setLoading(false);
    }
  };

  const fetchPreviousChecklists = async () => {
    // TODO: Implement once we have cross-race checklist data
    const mockPrevious = [
      {
        communityId: 'race_1',
        communityName: 'Boston Marathon 2025',
        items: [
          { id: 'p1', text: 'Running shoes', completed: true },
          { id: 'p2', text: 'Race bib', completed: true }
        ]
      }
    ];
    setPreviousChecklists(mockPrevious);
    setShowCloneModal(true);
  };

  const saveMantra = async () => {
    try {
      await api.put(`/mantra/${raceId}`, { text: tempMantra });
      setMantra(tempMantra);
      setEditingMantra(false);
    } catch (error) {
      console.error('Error saving mantra:', error);
    }
  };

  const addChecklistItem = async () => {
    if (!newItem.trim()) return;
    try {
      const res = await api.post(`/checklist/${raceId}`, {
        text: newItem.trim(),
        sortOrder: checklist.length,
        category: newItemCategory || 'Custom',
      });
      setChecklist(prev => [...prev, res.data]);
      setNewItem('');
    } catch (error) {
      console.error('Error adding checklist item:', error);
    }
  };

  const toggleChecklistItem = async (item) => {
    // Optimistic update — toggle immediately, revert on error
    const newCompleted = !item.completed;
    setChecklist(prev => prev.map(i => i.id === item.id ? { ...i, completed: newCompleted } : i));
    try {
      const res = await api.patch(`/checklist/${item.id}`, { completed: newCompleted });
      setChecklist(prev => prev.map(i => i.id === item.id ? res.data : i));
    } catch (error) {
      // Revert on failure
      setChecklist(prev => prev.map(i => i.id === item.id ? { ...i, completed: !newCompleted } : i));
      console.error('Error toggling item:', error);
    }
  };

  const deleteChecklistItem = async (itemId) => {
    // Optimistic update — remove immediately, revert on error
    const removed = checklist.find(i => i.id === itemId);
    setChecklist(prev => prev.filter(i => i.id !== itemId));
    try {
      await api.delete(`/checklist/item/${itemId}`);
    } catch (error) {
      if (removed) setChecklist(prev => [...prev, removed]);
      console.error('Error deleting item:', error);
    }
  };

  const cloneChecklist = async (sourceChecklist) => {
    try {
      const newItems = [];
      for (const item of sourceChecklist.items) {
        const res = await api.post(`/checklist/${raceId}`, {
          text: item.text,
          sortOrder: checklist.length + newItems.length,
        });
        newItems.push(res.data);
      }
      setChecklist(prev => [...prev, ...newItems]);
      setShowCloneModal(false);
    } catch (error) {
      console.error('Error cloning checklist:', error);
    }
  };

  const resetTravelForm = () => {
    setTravelForm({
      type: 'flight', confirmation: '',
      airline: '', flight_number: '', departure_airport: '', arrival_airport: '',
      departure_date: '', departure_time: '', return_date: '', return_time: '',
      company: '', pickup_location: '', pickup_date: '', pickup_time: '',
      dropoff_location: '', dropoff_date: '', dropoff_time: '',
      stay_name: '', address: '', checkin_date: '', checkout_date: '',
      description: '', start_date: '', start_time: '', end_date: '', end_time: '', notes: '',
    });
    setEditingTravelItem(null);
  };

  const openTravelModal = (item = null) => {
    if (item) {
      setEditingTravelItem(item);
      setTravelForm({
        type: item.type || 'other',
        confirmation: item.confirmation || '',
        airline: item.airline || '', flight_number: item.flight_number || '',
        departure_airport: item.departure_airport || '', arrival_airport: item.arrival_airport || '',
        departure_date: item.departure_date ? item.departure_date.split('T')[0] : '',
        departure_time: item.departure_time || '',
        return_date: item.return_date ? item.return_date.split('T')[0] : '',
        return_time: item.return_time || '',
        company: item.company || '', pickup_location: item.pickup_location || '',
        pickup_date: item.pickup_date ? item.pickup_date.split('T')[0] : '',
        pickup_time: item.pickup_time || '',
        dropoff_location: item.dropoff_location || '',
        dropoff_date: item.dropoff_date ? item.dropoff_date.split('T')[0] : '',
        dropoff_time: item.dropoff_time || '',
        stay_name: item.stay_name || '', address: item.address || '',
        checkin_date: item.checkin_date ? item.checkin_date.split('T')[0] : '',
        checkout_date: item.checkout_date ? item.checkout_date.split('T')[0] : '',
        description: item.description || '',
        start_date: item.start_date ? item.start_date.split('T')[0] : '',
        start_time: item.start_time || '',
        end_date: item.end_date ? item.end_date.split('T')[0] : '',
        end_time: item.end_time || '',
        notes: item.notes || '',
      });
    } else {
      resetTravelForm();
    }
    setShowTravelModal(true);
  };

  const isTravelFormValid = () => {
    switch (travelForm.type) {
      case 'flight': return !!(travelForm.airline || travelForm.flight_number);
      case 'car': return !!travelForm.company;
      case 'stay': return !!travelForm.stay_name;
      case 'other': return !!travelForm.description;
      default: return false;
    }
  };

  const saveTravelDetail = async () => {
    if (!isTravelFormValid()) return;
    const payload = { ...travelForm };
    // Clean empty strings to null for date fields
    ['departure_date', 'return_date', 'pickup_date', 'dropoff_date', 'checkin_date', 'checkout_date', 'start_date', 'end_date'].forEach(k => {
      if (!payload[k]) payload[k] = null;
    });

    try {
      if (editingTravelItem) {
        const res = await api.put(`/travel/${raceId}/${editingTravelItem.id}`, payload);
        setTravelDetails(travelDetails.map(t => t.id === editingTravelItem.id ? res.data : t));
      } else {
        const res = await api.post(`/travel/${raceId}`, payload);
        setTravelDetails([...travelDetails, res.data]);
      }
      setShowTravelModal(false);
      resetTravelForm();
    } catch (error) {
      console.error('Error saving travel detail:', error);
    }
  };

  const deleteTravelDetail = async (travelId) => {
    try {
      await api.delete(`/travel/${travelId}`);
      setTravelDetails(travelDetails.filter(t => t.id !== travelId));
    } catch (error) {
      console.error('Error deleting travel:', error);
    }
  };

  const parseConfirmationText = (text) => {
    const lower = text.toLowerCase();
    const result = {
      type: 'other', confirmation: '',
      airline: '', flight_number: '', departure_airport: '', arrival_airport: '',
      departure_date: '', departure_time: '', return_date: '', return_time: '',
      company: '', pickup_location: '', pickup_date: '', pickup_time: '',
      dropoff_location: '', dropoff_date: '', dropoff_time: '',
      stay_name: '', address: '', checkin_date: '', checkout_date: '',
      description: '', start_date: '', start_time: '', end_date: '', end_time: '', notes: '',
    };

    if (/\b(flight|airline|depart|arrive|boarding|terminal|gate)\b/.test(lower)) {
      result.type = 'flight';
    } else if (/\b(hotel|resort|inn|suite|check.?in|check.?out|room|reservation|airbnb|vrbo|lodge|motel)\b/.test(lower)) {
      result.type = 'stay';
    } else if (/\b(rental car|car rental|vehicle|pickup|hertz|avis|enterprise|budget|national)\b/.test(lower)) {
      result.type = 'car';
    } else if (/\b(train|rail|amtrak|seat\s?\d|coach|platform|bus|shuttle)\b/.test(lower)) {
      result.type = 'other';
    }

    const confPatterns = [
      /confirmation\s*(?:#|number|code|no\.?)?\s*:?\s*([A-Z0-9]{4,10})/i,
      /booking\s*(?:#|number|code|ref|reference|no\.?)?\s*:?\s*([A-Z0-9]{4,10})/i,
      /reservation\s*(?:#|number|code|no\.?)?\s*:?\s*([A-Z0-9]{4,10})/i,
      /record\s*locator\s*:?\s*([A-Z0-9]{4,10})/i,
      /PNR\s*:?\s*([A-Z0-9]{4,10})/i,
      /(?:ref|reference)\s*(?:#|number|code|no\.?)?\s*:?\s*([A-Z0-9]{4,10})/i
    ];
    for (const pattern of confPatterns) {
      const match = text.match(pattern);
      if (match) {
        result.confirmation = match[1];
        break;
      }
    }

    const parseDateFromText = (txt) => {
      const datePatterns = [
        /(\w+ \d{1,2},?\s*\d{4})/, /(\d{1,2}\/\d{1,2}\/\d{2,4})/,
        /(\d{4}-\d{2}-\d{2})/, /(\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\s+\d{4})/i,
      ];
      for (const pattern of datePatterns) {
        const match = txt.match(pattern);
        if (match) {
          const parsed = new Date(match[1]);
          if (!isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0];
        }
      }
      return '';
    };

    const foundDate = parseDateFromText(text);

    if (result.type === 'flight') {
      const flightMatch = text.match(/((?:Delta|United|American|Southwest|JetBlue|Spirit|Alaska|Frontier|Hawaiian|Sun Country|Allegiant)\s*(?:Air(?:lines?)?)?)\s*(?:flight|flt)?\s*#?\s*(\d{1,5})/i)
        || text.match(/([A-Za-z\s]+?)\s*(?:flight|flt)?\s*#?\s*(\d{1,5})/i);
      if (flightMatch) {
        result.airline = flightMatch[1].trim();
        result.flight_number = flightMatch[2];
      }
      const routeMatch = text.match(/([A-Z]{3})\s*(?:to|→|->|-)\s*([A-Z]{3})/);
      if (routeMatch) {
        result.departure_airport = routeMatch[1];
        result.arrival_airport = routeMatch[2];
      }
      if (foundDate) result.departure_date = foundDate;
    } else if (result.type === 'stay') {
      const hotelMatch = text.match(/((?:Hotel|Resort|Inn|Suites?|Lodge|Motel|Hilton|Marriott|Hyatt|Sheraton|Westin|Hampton|Holiday Inn|Courtyard|Fairfield|Residence Inn|SpringHill|Best Western|Comfort Inn|La Quinta|Radisson|Wyndham|IHG|Airbnb|VRBO)[^\n,]*)/i);
      if (hotelMatch) result.stay_name = hotelMatch[1].trim();
      if (foundDate) result.checkin_date = foundDate;
    } else if (result.type === 'car') {
      const carMatch = text.match(/((?:Hertz|Avis|Enterprise|Budget|National|Alamo|Dollar|Thrifty|Sixt|Zipcar)[^\n,]*)/i);
      if (carMatch) result.company = carMatch[1].trim();
      if (foundDate) result.pickup_date = foundDate;
    } else {
      const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 5 && l.length < 100);
      if (lines.length > 0) result.description = lines[0];
      if (foundDate) result.start_date = foundDate;
    }

    return result;
  };

  const handleParsePaste = () => {
    if (!pasteText.trim()) return;
    const parsed = parseConfirmationText(pasteText);
    setEditingTravelItem(null);
    setTravelForm(parsed);
    setShowPasteModal(false);
    setPasteText('');
    setShowTravelModal(true);
  };

  const saveStrategy = async () => {
    try {
      await api.put(`/strategy/${raceId}`, {
        timeGoal: tempStrategy.timeGoal,
        fuelPlan: tempStrategy.fuelPlan,
        raceStrategy: tempStrategy.raceStrategy,
      });
      setTimeGoal(tempStrategy.timeGoal);
      setFuelPlan(tempStrategy.fuelPlan);
      setRaceStrategy(tempStrategy.raceStrategy);
      setEditingStrategy(false);
    } catch (error) {
      console.error('Error saving strategy:', error);
    }
  };

  // ───── Pace Splits ─────
  const formatPace = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const formatTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const showSaved = (msg = 'Saved') => {
    setSaveMessage(msg);
    setTimeout(() => setSaveMessage(''), 2000);
  };

  const parsePaceInput = (input) => {
    const parts = input.split(':');
    if (parts.length === 2) {
      const m = parseInt(parts[0]) || 0;
      const s = parseInt(parts[1]) || 0;
      return m * 60 + s;
    }
    return null;
  };

  const updateMilePace = (mileNum, newPaceSeconds) => {
    const newSplits = paceSplits.splits.map((split, idx) => {
      const updatedPace = split.mile === mileNum ? newPaceSeconds : split.paceSeconds;
      const isLast = idx === paceSplits.splits.length - 1;
      const partialMile = paceSplits.distanceMiles - Math.floor(paceSplits.distanceMiles);
      const mileDistance = (isLast && partialMile > 0.01) ? partialMile : 1;
      return { ...split, paceSeconds: updatedPace, _distance: mileDistance };
    });
    // Recalculate cumulative times
    let cumulative = 0;
    for (const split of newSplits) {
      cumulative += Math.round(split.paceSeconds * split._distance);
      split.cumulativeSeconds = cumulative;
      delete split._distance;
    }
    setHasCustomSplits(true);
    setPaceSplits({ ...paceSplits, splits: newSplits });
    // Auto-save custom splits
    saveCustomSplits(newSplits);
  };

  const customSplitsSaveRef = useRef(null);
  const saveCustomSplits = (splits) => {
    if (customSplitsSaveRef.current) clearTimeout(customSplitsSaveRef.current);
    customSplitsSaveRef.current = setTimeout(async () => {
      try {
        await api.put(`/strategy/${raceId}/splits`, {
          goalTimeSeconds: paceSplits.goalTimeSeconds,
          distanceMiles: paceSplits.distanceMiles,
          pacingStrategy: paceSplits.pacingStrategy,
          customSplits: splits,
        });
        showSaved('Splits saved');
      } catch (error) {
        console.error('Error saving custom splits:', error);
      }
    }, 800);
  };

  const resetSplits = async () => {
    const goalTimeSeconds = splitsGoalHours * 3600 + splitsGoalMinutes * 60 + splitsGoalSeconds;
    if (goalTimeSeconds <= 0) return;
    try {
      const res = await api.put(`/strategy/${raceId}/splits`, {
        goalTimeSeconds,
        distanceMiles: paceSplits.distanceMiles,
        pacingStrategy: paceSplits.pacingStrategy,
      });
      setPaceSplits(res.data);
      setHasCustomSplits(false);
      showSaved('Splits reset');
    } catch (error) {
      console.error('Error resetting splits:', error);
    }
  };

  const savePaceSplits = async () => {
    const goalTimeSeconds = splitsGoalHours * 3600 + splitsGoalMinutes * 60 + splitsGoalSeconds;
    try {
      const res = await api.put(`/strategy/${raceId}/splits`, {
        goalTimeSeconds,
        distanceMiles: paceSplits.distanceMiles,
        pacingStrategy: paceSplits.pacingStrategy,
      });
      setPaceSplits(res.data);
      setHasCustomSplits(false);
      showSaved('Splits saved');
    } catch (error) {
      console.error('Error saving splits:', error);
    }
  };

  // ───── Triathlon Pacer ─────
  const triSaveTimerRef = useRef(null);
  const saveTriathlonLegs = (updatedLegs) => {
    if (triSaveTimerRef.current) clearTimeout(triSaveTimerRef.current);
    triSaveTimerRef.current = setTimeout(async () => {
      const totalSeconds = updatedLegs.swimSeconds + updatedLegs.t1Seconds + updatedLegs.bikeSeconds + updatedLegs.t2Seconds + updatedLegs.runSeconds;
      try {
        const res = await api.put(`/strategy/${raceId}/splits`, {
          isTriathlon: true,
          triathlonLegs: updatedLegs,
          goalTimeSeconds: totalSeconds,
          distanceMiles: paceSplits.distanceMiles,
          pacingStrategy: 'even',
        });
        setPaceSplits(res.data);
        showSaved('Pacer saved');
      } catch (error) {
        console.error('Error saving triathlon legs:', error);
      }
    }, 800);
  };

  const updateTriLeg = (field, value) => {
    const updated = { ...triathlonLegs, [field]: value };
    setTriathlonLegs(updated);
    saveTriathlonLegs(updated);
  };

  // ───── Race Day Timeline ─────
  const generateSuggestedTimeline = () => {
    const gunTime = timeline.gunTime;
    if (!gunTime) return;
    const parts = gunTime.split(':').map(Number);
    if (parts.length < 2) return;
    const gunMinutes = parts[0] * 60 + parts[1];

    const offsets = [
      [-180, 'Wake up', 'prep'],
      [-150, 'Breakfast', 'prep'],
      [-90, 'Leave for venue', 'travel'],
      [-60, 'Arrive at venue', 'travel'],
      [-30, 'Warm up', 'prep'],
      [-15, 'Head to corral', 'race'],
      [0, 'Gun time', 'race'],
    ];

    const events = offsets.map(([offset, label, type]) => {
      let mins = gunMinutes + offset;
      if (mins < 0) mins += 24 * 60;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return { time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`, label, type, notes: '' };
    });

    setTimeline({ ...timeline, events });
  };

  const addTimelineEvent = () => {
    if (!timelineEventForm.label.trim()) return;
    const newEvents = [...timeline.events, { ...timelineEventForm }]
      .sort((a, b) => a.time.localeCompare(b.time));
    setTimeline({ ...timeline, events: newEvents });
    setTimelineEventForm({ time: '', label: '', type: 'prep', notes: '' });
    setShowTimelineEventModal(false);
  };

  const deleteTimelineEvent = (index) => {
    const newEvents = timeline.events.filter((_, i) => i !== index);
    setTimeline({ ...timeline, events: newEvents });
  };

  const saveTimeline = async () => {
    try {
      await api.put(`/strategy/${raceId}/timeline`, {
        events: timeline.events,
        gunTime: timeline.gunTime,
        notes: timeline.notes,
      });
      showSaved('Timeline saved');
    } catch (error) {
      console.error('Error saving timeline:', error);
    }
  };

  const timelineTypeColor = (type) => {
    switch (type) {
      case 'prep': return '#4A90D9';
      case 'travel': return '#FF8A3D';
      case 'race': return '#FF8A3D';
      default: return '#888';
    }
  };

  // ───── Reflection ─────
  const isPastRace = race.end_date && new Date(race.end_date) < new Date();

  const saveReflection = async () => {
    const actualTimeSeconds = reflectionHours * 3600 + reflectionMinutes * 60 + reflectionSeconds;
    try {
      const res = await api.put(`/strategy/${raceId}/reflection`, {
        actualTimeSeconds: actualTimeSeconds > 0 ? actualTimeSeconds : null,
        overallRating: reflection.overallRating,
        whatWorked: reflection.whatWorked,
        whatDidnt: reflection.whatDidnt,
        fuelNotes: reflection.fuelNotes,
        weatherImpact: reflection.weatherImpact,
        wouldChange: reflection.wouldChange,
        highlight: reflection.highlight,
      });
      setReflection(res.data);
      showSaved('Reflection saved');
    } catch (error) {
      console.error('Error saving reflection:', error);
    }
  };

  const saveCharity = async () => {
    try {
      await api.put(`/registrations/${raceId}/charity`, {
        charity_name: charityName.trim(),
        fundraising_url: fundraisingUrl.trim(),
      });
      setEditingCharity(false);
      showSaved('Charity info saved');
    } catch (error) {
      console.error('Error saving charity:', error);
    }
  };

  const toggleCategory = (cat) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const getGroupedChecklist = () => {
    const groups = {};
    const categoryOrder = ['Gear', 'Admin', 'Body Care', 'Fuel', 'Custom'];
    checklist.forEach(item => {
      const cat = item.category || 'Custom';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    const ordered = [];
    categoryOrder.forEach(cat => {
      if (groups[cat]) ordered.push({ category: cat, items: groups[cat] });
    });
    Object.keys(groups).forEach(cat => {
      if (!categoryOrder.includes(cat)) ordered.push({ category: cat, items: groups[cat] });
    });
    return ordered;
  };

  const resetNutritionForm = () => {
    setNutritionForm({
      brand_name: '', brand_id: null, flavor: '', timing: '', notes: '',
      is_homemade: false, homemade_name: '', homemade_ingredients: '', homemade_prep_notes: '',
      save_as_recipe: false,
    });
    setFuelSearch('');
    setEditingNutritionItem(null);
  };

  const openNutritionModal = (item = null) => {
    if (item) {
      setEditingNutritionItem(item);
      setNutritionForm({
        brand_name: item.brand_name || item.directory_brand_name || '',
        brand_id: item.brand_id || null,
        flavor: item.flavor || '',
        timing: item.timing || '',
        notes: item.notes || '',
        is_homemade: item.is_homemade || false,
        homemade_name: item.homemade_name || '',
        homemade_ingredients: item.homemade_ingredients || '',
        homemade_prep_notes: item.homemade_prep_notes || '',
        save_as_recipe: false,
      });
      setFuelSearch(item.brand_name || item.directory_brand_name || '');
    } else {
      resetNutritionForm();
    }
    setShowNutritionModal(true);
  };

  const saveNutritionItem = async () => {
    const payload = {
      brand_id: nutritionForm.brand_id,
      brand_name: nutritionForm.is_homemade ? '' : (nutritionForm.brand_name || fuelSearch),
      flavor: nutritionForm.flavor,
      timing: nutritionForm.timing,
      notes: nutritionForm.notes,
      sort_order: editingNutritionItem ? editingNutritionItem.sort_order : nutritionItems.length,
      is_homemade: nutritionForm.is_homemade,
      homemade_name: nutritionForm.homemade_name,
      homemade_ingredients: nutritionForm.homemade_ingredients,
      homemade_prep_notes: nutritionForm.homemade_prep_notes,
    };

    try {
      if (editingNutritionItem) {
        const res = await api.put(`/fuel/${raceId}/${editingNutritionItem.id}`, payload);
        setNutritionItems(nutritionItems.map(n => n.id === editingNutritionItem.id ? res.data : n));
      } else {
        const res = await api.post(`/fuel/${raceId}`, payload);
        setNutritionItems([...nutritionItems, res.data]);
      }

      if (nutritionForm.is_homemade && nutritionForm.save_as_recipe && nutritionForm.homemade_name) {
        await api.post('/fuel/recipes', {
          name: nutritionForm.homemade_name,
          ingredients: nutritionForm.homemade_ingredients,
          prep_notes: nutritionForm.homemade_prep_notes,
        });
        const recipesRes = await api.get('/fuel/recipes');
        setHomemadeRecipes(recipesRes.data);
      }

      setShowNutritionModal(false);
      resetNutritionForm();
    } catch (error) {
      console.error('Error saving nutrition item:', error);
    }
  };

  const deleteNutritionItem = async (itemId) => {
    try {
      await api.delete(`/fuel/${raceId}/${itemId}`);
      setNutritionItems(nutritionItems.filter(n => n.id !== itemId));
    } catch (error) {
      console.error('Error deleting nutrition item:', error);
    }
  };

  const loadHomemadeRecipes = async () => {
    try {
      const res = await api.get('/fuel/recipes');
      setHomemadeRecipes(res.data);
    } catch (error) {
      console.error('Error loading recipes:', error);
    }
  };

  const selectRecipe = (recipe) => {
    setNutritionForm(prev => ({
      ...prev,
      homemade_name: recipe.name,
      homemade_ingredients: recipe.ingredients,
      homemade_prep_notes: recipe.prep_notes,
    }));
  };

  const filteredBrands = fuelSearch.trim()
    ? fuelBrandDirectory.filter(b => b.name.toLowerCase().includes(fuelSearch.toLowerCase()))
    : fuelBrandDirectory;

  const getTravelIcon = (type) => {
    switch (type) {
      case 'flight': return '✈️';
      case 'stay': case 'hotel': return '🏨';
      case 'car': return '🚗';
      default: return '📍';
    }
  };

  const isLegacyTravel = (travel) => {
    return !travel.airline && !travel.flight_number && !travel.departure_airport &&
      !travel.company && !travel.pickup_location &&
      !travel.stay_name && !travel.address &&
      !travel.description && !travel.start_date;
  };

  const getTravelTitle = (travel) => {
    if (isLegacyTravel(travel)) return travel.details;
    switch (travel.type) {
      case 'flight': {
        const parts = [travel.airline, travel.flight_number].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : 'Flight';
      }
      case 'car':
        return travel.company || 'Rental Car';
      case 'stay':
        return travel.stay_name || 'Stay';
      case 'other':
        return travel.description || 'Other';
      default:
        return travel.details || 'Travel';
    }
  };

  const getTravelSubtitle = (travel) => {
    if (isLegacyTravel(travel)) return null;
    switch (travel.type) {
      case 'flight':
        if (travel.departure_airport && travel.arrival_airport) {
          return `${travel.departure_airport} → ${travel.arrival_airport}`;
        }
        return null;
      case 'car':
        return [travel.pickup_location, travel.dropoff_location].filter(Boolean).join(' → ') || null;
      case 'stay':
        return travel.address || null;
      case 'other':
        return travel.notes || null;
      default:
        return null;
    }
  };

  const getTravelDateDisplay = (travel) => {
    if (isLegacyTravel(travel)) {
      return travel.date ? formatDate(travel.date, 'compact') : null;
    }
    switch (travel.type) {
      case 'flight': {
        const dep = travel.departure_date ? formatDate(travel.departure_date, 'compact') : '';
        const depTime = travel.departure_time || '';
        const ret = travel.return_date ? formatDate(travel.return_date, 'compact') : '';
        const retTime = travel.return_time || '';
        const depStr = [dep, depTime].filter(Boolean).join(' ');
        const retStr = [ret, retTime].filter(Boolean).join(' ');
        if (depStr && retStr) return `${depStr}  →  ${retStr}`;
        return depStr || retStr || null;
      }
      case 'car': {
        const pick = travel.pickup_date ? formatDate(travel.pickup_date, 'compact') : '';
        const pickTime = travel.pickup_time || '';
        const drop = travel.dropoff_date ? formatDate(travel.dropoff_date, 'compact') : '';
        const dropTime = travel.dropoff_time || '';
        const pickStr = [pick, pickTime].filter(Boolean).join(' ');
        const dropStr = [drop, dropTime].filter(Boolean).join(' ');
        if (pickStr && dropStr) return `${pickStr}  →  ${dropStr}`;
        return pickStr || dropStr || null;
      }
      case 'stay': {
        const ci = travel.checkin_date ? formatDate(travel.checkin_date, 'compact') : '';
        const co = travel.checkout_date ? formatDate(travel.checkout_date, 'compact') : '';
        if (ci && co) return `${ci}  →  ${co}`;
        return ci || co || null;
      }
      case 'other': {
        const s = travel.start_date ? formatDate(travel.start_date, 'compact') : '';
        const sTime = travel.start_time || '';
        const e = travel.end_date ? formatDate(travel.end_date, 'compact') : '';
        const eTime = travel.end_time || '';
        const sStr = [s, sTime].filter(Boolean).join(' ');
        const eStr = [e, eTime].filter(Boolean).join(' ');
        if (sStr && eStr) return `${sStr}  →  ${eStr}`;
        return sStr || eStr || null;
      }
      default:
        return travel.date ? formatDate(travel.date, 'compact') : null;
    }
  };

  const countdown = calculateCountdown(race.end_date);
  const completedItems = checklist.filter(i => i.completed).length;

  if (loading) {
    return <LoadingScreen message="Loading race details..." />;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B0B10',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        background: raceReadyMode ? '#0B0B10' : 'linear-gradient(135deg, #0B0B10 0%, #1E1E28 100%)',
        padding: isMobile ? '1rem' : '1rem 2rem',
        borderBottom: '1px solid rgba(255, 138, 61, 0.15)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Left side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {raceReadyMode && (
              <img
                src={`${process.env.PUBLIC_URL}/runrora-logo.png`}
                alt="Runrora"
                style={{
                  height: '40px',
                  width: 'auto',
                  marginRight: '1rem'
                }}
              />
            )}
            <button
              onClick={onBack}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#FF8A3D',
                cursor: 'pointer',
                fontSize: '1.5rem',
                padding: '0.5rem'
              }}
            >
              ←
            </button>
            <div>
              <h1 style={{
                fontSize: '1.5rem',
                fontFamily: 'Instrument Serif, serif',
                color: '#FFFFFF',
                margin: 0
              }}>
                {race.name}
              </h1>
              <p style={{ color: '#9ca3af', margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                📍 {race.location}
              </p>
            </div>
          </div>

          {/* Right side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {raceReadyMode && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.4rem 0.75rem',
                background: 'rgba(255, 138, 61, 0.15)',
                border: '1px solid rgba(255, 138, 61, 0.3)',
                borderRadius: '20px'
              }}>
                <span style={{ fontSize: '0.85rem' }}>🏁</span>
                <span style={{ color: '#FF8A3D', fontSize: '0.75rem', fontWeight: 600 }}>
                  Race-Ready Mode
                </span>
              </div>
            )}

            <div style={{
              textAlign: 'center',
              padding: '0.75rem 1.25rem',
              background: countdown.days <= 7 && !countdown.isPast
                ? 'rgba(255, 138, 61, 0.3)'
                : 'rgba(255, 255, 255, 0.1)',
              borderRadius: '12px'
            }}>
              <div style={{
                fontSize: '1.75rem',
                fontWeight: 700,
                color: countdown.days <= 7 && !countdown.isPast ? '#FF8A3D' : '#FFFFFF',
                lineHeight: 1
              }}>
                {!countdown.isPast ? countdown.days : '🎉'}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {!countdown.isPast ? (countdown.days === 1 ? 'day left' : 'days left') : 'Race day!'}
              </div>
            </div>

            {raceReadyMode && (
              <ProfileDropdown
                currentUserId={user?.id}
                userName={user?.name}
                raceReadyMode={raceReadyMode}
                onToggleRaceReady={onToggleRaceReady}
                onLogout={onLogout}
                showProfileDropdown={showProfileDropdown}
                setShowProfileDropdown={setShowProfileDropdown}
              />
            )}
          </div>
        </div>
      </div>

      {/* Hero with Mantra */}
      <div style={{
        background: `linear-gradient(rgba(11, 11, 16, 0.7), rgba(11, 11, 16, 0.9)), url(${race.image_url}) center/cover`,
        padding: isMobile ? '2rem 1rem' : '3rem 2rem',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          {editingMantra ? (
            <div>
              {showMantraPicker ? (
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <button
                      onClick={() => setShowMantraPicker(false)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#FF8A3D',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        padding: 0
                      }}
                    >
                      &larr; Back to editor
                    </button>
                  </div>
                  {MANTRA_PRESETS.map((group) => (
                    <div key={group.theme} style={{ marginBottom: '1.25rem' }}>
                      <h4 style={{
                        color: '#FF8A3D',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        margin: '0 0 0.5rem 0'
                      }}>
                        {group.theme}
                      </h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {group.mantras.map((m) => (
                          <button
                            key={m}
                            onClick={() => {
                              setTempMantra(m);
                              setShowMantraPicker(false);
                            }}
                            style={{
                              padding: '0.4rem 0.85rem',
                              background: 'rgba(255, 255, 255, 0.08)',
                              border: '1px solid rgba(255, 138, 61, 0.25)',
                              borderRadius: '20px',
                              color: '#FFFFFF',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontStyle: 'italic',
                              transition: 'background 0.15s, border-color 0.15s'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(255, 138, 61, 0.2)';
                              e.target.style.borderColor = '#FF8A3D';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                              e.target.style.borderColor = 'rgba(255, 138, 61, 0.25)';
                            }}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <textarea
                    value={tempMantra}
                    onChange={(e) => setTempMantra(e.target.value)}
                    placeholder="Enter your race mantra..."
                    style={{
                      width: '100%',
                      padding: '1rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      fontSize: '1.25rem',
                      textAlign: 'center',
                      fontStyle: 'italic',
                      resize: 'none',
                      outline: 'none',
                      fontFamily: 'inherit'
                    }}
                    rows={2}
                  />
                  <button
                    onClick={() => setShowMantraPicker(true)}
                    style={{
                      marginTop: '0.75rem',
                      padding: '0.4rem 1rem',
                      background: 'transparent',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      color: '#FF8A3D',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    Browse Mantras
                  </button>
                </>
              )}
              <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button
                  onClick={() => { setEditingMantra(false); setShowMantraPicker(false); }}
                  style={{
                    padding: '0.5rem 1.5rem',
                    background: 'transparent',
                    border: '1px solid rgba(255, 138, 61, 0.3)',
                    color: '#9ca3af',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveMantra}
                  style={{
                    padding: '0.5rem 1.5rem',
                    background: '#FF8A3D',
                    border: 'none',
                    color: '#0B0B10',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Save Mantra
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => {
                setTempMantra(mantra);
                setEditingMantra(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              {mantra ? (
                <p style={{
                  color: '#FFFFFF',
                  fontSize: '1.5rem',
                  fontStyle: 'italic',
                  margin: 0,
                  lineHeight: 1.6
                }}>
                  "{mantra}"
                </p>
              ) : (
                <p style={{
                  color: '#9ca3af',
                  fontSize: '1.1rem',
                  margin: 0
                }}>
                  ✨ Click to add your race mantra
                </p>
              )}
              <p style={{
                color: '#6b7280',
                fontSize: '0.8rem',
                marginTop: '0.5rem'
              }}>
                {mantra ? 'Click to edit' : 'A personal motto to inspire you on race day'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '0 0.75rem' : '0 2rem'
      }}>
        <div style={{
          display: 'flex',
          gap: isMobile ? '0.75rem' : '1.5rem',
          borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}>
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'strategy', label: 'Strategy' },
            { key: 'checklist', label: `Checklist (${completedItems}/${checklist.length})` },
            { key: 'nutrition', label: `Nutrition (${nutritionItems.length})` },
            { key: 'travel', label: `Travel (${travelDetails.length})` },
            { key: 'raceday', label: 'Race Day' },
            { key: 'reflection', label: 'Reflection' },
            ...(isPastRace ? [{ key: 'stories', label: 'Stories' }] : [])
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: isMobile ? '0.75rem 0' : '1rem 0',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.key ? '2px solid #FF8A3D' : '2px solid transparent',
                color: activeTab === tab.key ? '#FF8A3D' : '#9ca3af',
                cursor: 'pointer',
                fontSize: isMobile ? '0.8rem' : '0.95rem',
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '1rem' : '2rem'
      }}>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            <div
              onClick={() => setActiveTab('checklist')}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 138, 61, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer'
              }}
            >
              <h3 style={{ color: '#FFFFFF', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                📋 Packing Checklist
              </h3>
              {checklist.length === 0 ? (
                <p style={{ color: '#9ca3af', margin: 0 }}>No items yet. Start packing!</p>
              ) : (
                <>
                  <div style={{
                    background: 'rgba(255, 138, 61, 0.2)',
                    borderRadius: '8px',
                    height: '8px',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{
                      background: '#FF8A3D',
                      borderRadius: '8px',
                      height: '100%',
                      width: `${(completedItems / checklist.length) * 100}%`,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  <p style={{ color: '#9ca3af', margin: 0, fontSize: '0.9rem' }}>
                    {completedItems} of {checklist.length} items packed
                  </p>
                </>
              )}
            </div>

            <div
              onClick={() => setActiveTab('travel')}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 138, 61, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer'
              }}
            >
              <h3 style={{ color: '#FFFFFF', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                🧳 Travel Plans
              </h3>
              {travelDetails.length === 0 ? (
                <p style={{ color: '#9ca3af', margin: 0 }}>No travel details yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {travelDetails.slice(0, 3).map((travel) => (
                    <div key={travel.id} style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                      {getTravelIcon(travel.type)} {getTravelTitle(travel)}
                    </div>
                  ))}
                  {travelDetails.length > 3 && (
                    <p style={{ color: '#6b7280', margin: 0, fontSize: '0.85rem' }}>
                      +{travelDetails.length - 3} more
                    </p>
                  )}
                </div>
              )}
            </div>

            <div
              onClick={() => setActiveTab('nutrition')}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 138, 61, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer'
              }}
            >
              <h3 style={{ color: '#FFFFFF', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                ⚡ Nutrition Plan
              </h3>
              {nutritionItems.length === 0 ? (
                <p style={{ color: '#9ca3af', margin: 0 }}>Plan your race day fuel.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {nutritionItems.slice(0, 3).map((item) => (
                    <div key={item.id} style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                      {item.is_homemade ? item.homemade_name : (item.directory_brand_name || item.brand_name)}
                      {item.timing && <span style={{ color: '#6b7280' }}> - {item.timing}</span>}
                    </div>
                  ))}
                  {nutritionItems.length > 3 && (
                    <p style={{ color: '#6b7280', margin: 0, fontSize: '0.85rem' }}>
                      +{nutritionItems.length - 3} more
                    </p>
                  )}
                </div>
              )}
            </div>

            <div
              onClick={() => setActiveTab('strategy')}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 138, 61, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer'
              }}
            >
              <h3 style={{ color: '#FFFFFF', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                🎯 Race Day Strategy
              </h3>
              {!timeGoal && !fuelPlan && !raceStrategy ? (
                <p style={{ color: '#9ca3af', margin: 0 }}>Plan your race day approach.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {timeGoal && (
                    <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                      🏁 Goal: {timeGoal}
                    </div>
                  )}
                  {fuelPlan && (
                    <div style={{ color: '#9ca3af', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      ⛽ {fuelPlan}
                    </div>
                  )}
                  {raceStrategy && (
                    <div style={{ color: '#9ca3af', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      📝 {raceStrategy}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Pace Splits Card */}
            <div
              onClick={() => setActiveTab('strategy')}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 138, 61, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer'
              }}
            >
              <h3 style={{ color: '#FFFFFF', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                ⏱️ Pace Splits
              </h3>
              {paceSplits.goalTimeSeconds > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                    Goal: {formatTime(paceSplits.goalTimeSeconds)}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                    Pace: {formatPace(Math.round(paceSplits.goalTimeSeconds / paceSplits.distanceMiles))}/mi ({paceSplits.pacingStrategy})
                  </div>
                </div>
              ) : (
                <p style={{ color: '#9ca3af', margin: 0 }}>Set your goal time and pacing strategy.</p>
              )}
            </div>

            {/* Race Day Card */}
            <div
              onClick={() => setActiveTab('raceday')}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 138, 61, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer'
              }}
            >
              <h3 style={{ color: '#FFFFFF', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                🌅 Race Day Timeline
              </h3>
              {timeline.events.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {timeline.gunTime && (
                    <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                      Gun time: {timeline.gunTime}
                    </div>
                  )}
                  <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                    {timeline.events.length} events planned
                  </div>
                </div>
              ) : (
                <p style={{ color: '#9ca3af', margin: 0 }}>Plan your race morning schedule.</p>
              )}
            </div>

            {/* Reflection Card */}
            {isPastRace && (
              <div
                onClick={() => setActiveTab('reflection')}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 138, 61, 0.2)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  cursor: 'pointer'
                }}
              >
                <h3 style={{ color: '#FFFFFF', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                  📝 Race Reflection
                </h3>
                {reflection.overallRating ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ color: '#FF8A3D', fontSize: '1rem' }}>
                      {'★'.repeat(reflection.overallRating)}{'☆'.repeat(5 - reflection.overallRating)}
                    </div>
                    {reflection.actualTimeSeconds && (
                      <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                        Finished in {formatTime(reflection.actualTimeSeconds)}
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ color: '#9ca3af', margin: 0 }}>Reflect on your race performance.</p>
                )}
              </div>
            )}

            {/* Charity & Fundraising Card */}
            <div
              onClick={() => !editingCharity && setEditingCharity(true)}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(244, 114, 182, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: editingCharity ? 'default' : 'pointer'
              }}
            >
              <h3 style={{ color: '#FFFFFF', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                {'\u2764\uFE0F'} Charity & Fundraising
              </h3>
              {editingCharity ? (
                <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  <input
                    type="text"
                    value={charityName}
                    onChange={(e) => setCharityName(e.target.value)}
                    placeholder="Charity name"
                    maxLength={100}
                    style={{
                      width: '100%', padding: '0.6rem 0.75rem',
                      background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px', color: '#FFFFFF', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                  <input
                    type="url"
                    value={fundraisingUrl}
                    onChange={(e) => setFundraisingUrl(e.target.value)}
                    placeholder="Fundraising page URL"
                    style={{
                      width: '100%', padding: '0.6rem 0.75rem',
                      background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px', color: '#FFFFFF', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={saveCharity} style={{
                      padding: '0.5rem 1rem', background: '#FF8A3D', border: 'none',
                      color: '#0B0B10', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem'
                    }}>Save</button>
                    <button onClick={() => setEditingCharity(false)} style={{
                      padding: '0.5rem 1rem', background: 'transparent', border: '1px solid rgba(255, 138, 61, 0.3)',
                      color: '#9ca3af', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem'
                    }}>Cancel</button>
                  </div>
                </div>
              ) : charityName ? (
                <div>
                  <span style={{ color: '#f472b6', fontWeight: 600 }}>{charityName}</span>
                  {fundraisingUrl && (
                    <a href={fundraisingUrl.startsWith('http') ? fundraisingUrl : `https://${fundraisingUrl}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ display: 'block', color: '#FF8A3D', fontSize: '0.85rem', marginTop: '0.25rem', textDecoration: 'none' }}>
                      View fundraising page {'\u2197'}
                    </a>
                  )}
                </div>
              ) : (
                <p style={{ color: '#9ca3af', margin: 0 }}>Running for a cause? Add your charity here.</p>
              )}
            </div>
          </div>
        )}

        {/* Strategy Tab */}
        {activeTab === 'strategy' && (
          <div>
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#FFFFFF', margin: 0 }}>Race Day Strategy</h3>
            </div>

            {editingStrategy ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Time Goal
                  </label>
                  <input
                    type="text"
                    value={tempStrategy.timeGoal}
                    onChange={(e) => setTempStrategy({ ...tempStrategy, timeGoal: e.target.value })}
                    placeholder="e.g., Sub 4:00, 3:45:00, finish strong"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Fuel Plan
                  </label>
                  <textarea
                    value={tempStrategy.fuelPlan}
                    onChange={(e) => setTempStrategy({ ...tempStrategy, fuelPlan: e.target.value })}
                    placeholder="e.g., Gel at miles 6, 12, 18. Water at every station. Salt tabs at 10 and 20."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      fontSize: '1rem',
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Race Strategy
                  </label>
                  <textarea
                    value={tempStrategy.raceStrategy}
                    onChange={(e) => setTempStrategy({ ...tempStrategy, raceStrategy: e.target.value })}
                    placeholder="e.g., Start at 9:00/mi pace, negative split, push last 10K."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      fontSize: '1rem',
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={() => setEditingStrategy(false)}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: 'transparent',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      color: '#9ca3af',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveStrategy}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: '#FF8A3D',
                      border: 'none',
                      color: '#0B0B10',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Save Strategy
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {!timeGoal && !fuelPlan && !raceStrategy ? (
                  <div
                    onClick={() => {
                      setTempStrategy({ timeGoal, fuelPlan, raceStrategy });
                      setEditingStrategy(true);
                    }}
                    style={{
                      textAlign: 'center',
                      padding: '3rem',
                      color: '#9ca3af',
                      background: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🎯</div>
                    <p style={{ marginBottom: '0.5rem' }}>No strategy set yet.</p>
                    <p style={{ color: '#FF8A3D', fontWeight: 600 }}>Click to plan your race day</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    {timeGoal && (
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 138, 61, 0.15)',
                        borderRadius: '12px',
                        padding: '1.25rem'
                      }}>
                        <div style={{ color: '#FF8A3D', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                          🏁 TIME GOAL
                        </div>
                        <div style={{ color: '#FFFFFF', fontSize: '1.5rem', fontWeight: 700 }}>
                          {timeGoal}
                        </div>
                      </div>
                    )}

                    {fuelPlan && (
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 138, 61, 0.15)',
                        borderRadius: '12px',
                        padding: '1.25rem'
                      }}>
                        <div style={{ color: '#FF8A3D', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                          ⛽ FUEL PLAN
                        </div>
                        <p style={{ color: '#FFFFFF', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {fuelPlan}
                        </p>
                      </div>
                    )}

                    {raceStrategy && (
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 138, 61, 0.15)',
                        borderRadius: '12px',
                        padding: '1.25rem'
                      }}>
                        <div style={{ color: '#FF8A3D', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                          📝 RACE STRATEGY
                        </div>
                        <p style={{ color: '#FFFFFF', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {raceStrategy}
                        </p>
                      </div>
                    )}

                    <button
                      onClick={() => {
                        setTempStrategy({ timeGoal, fuelPlan, raceStrategy });
                        setEditingStrategy(true);
                      }}
                      style={{
                        padding: '0.75rem',
                        background: 'transparent',
                        border: '1px solid rgba(255, 138, 61, 0.3)',
                        color: '#FF8A3D',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                    >
                      Edit Strategy
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Pace Calculator */}
            <div style={{ marginTop: '2rem' }}>
              <h3 style={{ color: '#FFFFFF', margin: '0 0 1rem 0' }}>
                {paceSplits.isTriathlon ? '⏱️ Race Pacer' : '⏱️ Pace Calculator'}
              </h3>
              {paceSplits.isTriathlon ? (
                /* ───── Triathlon Pacer ───── */
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 138, 61, 0.2)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {[
                    { label: '\u{1F3CA} Swim', field: 'swimSeconds' },
                    { label: 'T1', field: 't1Seconds' },
                    { label: '\u{1F6B4} Bike', field: 'bikeSeconds' },
                    { label: 'T2', field: 't2Seconds' },
                    { label: '\u{1F3C3} Run', field: 'runSeconds' },
                  ].map(({ label, field }) => {
                    const totalSec = triathlonLegs[field] || 0;
                    const h = Math.floor(totalSec / 3600);
                    const m = Math.floor((totalSec % 3600) / 60);
                    const s = totalSec % 60;
                    return (
                      <div key={field} style={{
                        display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem',
                        background: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.05)'
                      }}>
                        <div style={{ width: '70px', color: '#FFFFFF', fontWeight: 600, fontSize: '0.95rem', flexShrink: 0 }}>{label}</div>
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flex: 1 }}>
                          {[
                            { val: h, label: 'H', max: 23, calc: (v) => v * 3600 + m * 60 + s },
                            { val: m, label: 'MM', max: 59, calc: (v) => h * 3600 + v * 60 + s },
                            { val: s, label: 'SS', max: 59, calc: (v) => h * 3600 + m * 60 + v },
                          ].map(({ val, label: lbl, max, calc }, i) => (
                            <React.Fragment key={lbl}>
                              {i > 0 && <span style={{ color: '#9ca3af', fontWeight: 700 }}>:</span>}
                              <div style={{ flex: 1 }}>
                                <input type="number" min="0" max={max} value={val}
                                  onChange={(e) => updateTriLeg(field, calc(parseInt(e.target.value) || 0))}
                                  style={{ width: '100%', padding: '0.5rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '6px', color: '#FFFFFF', outline: 'none', textAlign: 'center', fontSize: '1rem' }}
                                />
                                <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.65rem', marginTop: '0.15rem' }}>{lbl}</div>
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {(() => {
                    const total = (triathlonLegs.swimSeconds || 0) + (triathlonLegs.t1Seconds || 0) + (triathlonLegs.bikeSeconds || 0) + (triathlonLegs.t2Seconds || 0) + (triathlonLegs.runSeconds || 0);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem', background: 'rgba(255, 138, 61, 0.1)', borderRadius: '8px', borderTop: '2px solid rgba(255, 138, 61, 0.3)' }}>
                        <div style={{ width: '70px', color: '#FF8A3D', fontWeight: 700, fontSize: '0.95rem', flexShrink: 0 }}>Total</div>
                        <div style={{ color: '#FF8A3D', fontSize: '1.25rem', fontWeight: 700 }}>{formatTime(total)}</div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* ───── Standard Pace Calculator ───── */
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 138, 61, 0.2)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1.5rem'
                }}>
                  {/* Goal Time */}
                  <div>
                    <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Goal Time</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input type="number" min="0" max="23" value={splitsGoalHours} onChange={(e) => setSplitsGoalHours(parseInt(e.target.value) || 0)} placeholder="H" style={{ width: '70px', padding: '0.6rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none', textAlign: 'center', fontSize: '1rem' }} />
                      <span style={{ color: '#9ca3af', fontWeight: 700 }}>:</span>
                      <input type="number" min="0" max="59" value={splitsGoalMinutes} onChange={(e) => setSplitsGoalMinutes(parseInt(e.target.value) || 0)} placeholder="MM" style={{ width: '70px', padding: '0.6rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none', textAlign: 'center', fontSize: '1rem' }} />
                      <span style={{ color: '#9ca3af', fontWeight: 700 }}>:</span>
                      <input type="number" min="0" max="59" value={splitsGoalSeconds || ''} onChange={(e) => setSplitsGoalSeconds(parseInt(e.target.value) || 0)} placeholder="SS" style={{ width: '70px', padding: '0.6rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none', textAlign: 'center', fontSize: '1rem' }} />
                    </div>
                  </div>

                  {/* Distance */}
                  <div>
                    <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Distance</label>
                    <select
                      value={[3.1, 6.2, 13.1, 26.2, 31.07, 50, 62.14, 100].includes(paceSplits.distanceMiles) ? paceSplits.distanceMiles : 'custom'}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === 'custom') { setPaceSplits({ ...paceSplits, distanceMiles: 0 }); }
                        else { setPaceSplits({ ...paceSplits, distanceMiles: parseFloat(val) }); }
                      }}
                      style={{ width: '100%', padding: '0.6rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none', fontSize: '0.95rem' }}
                    >
                      <option value={3.1}>5K (3.1 mi)</option>
                      <option value={6.2}>10K (6.2 mi)</option>
                      <option value={13.1}>Half Marathon (13.1 mi)</option>
                      <option value={26.2}>Marathon (26.2 mi)</option>
                      <option value={31.07}>50K (31.07 mi)</option>
                      <option value={50}>50 Mile</option>
                      <option value={62.14}>100K (62.14 mi)</option>
                      <option value={100}>100 Mile</option>
                      <option value="custom">Custom</option>
                    </select>
                    {paceSplits.distanceMiles !== 3.1 && paceSplits.distanceMiles !== 6.2 && paceSplits.distanceMiles !== 13.1 && paceSplits.distanceMiles !== 26.2 && paceSplits.distanceMiles !== 31.07 && paceSplits.distanceMiles !== 50 && paceSplits.distanceMiles !== 62.14 && paceSplits.distanceMiles !== 100 && (
                      <input type="number" step="0.1" min="0.1" placeholder="Enter distance in miles" value={paceSplits.distanceMiles || ''}
                        onChange={(e) => setPaceSplits({ ...paceSplits, distanceMiles: parseFloat(e.target.value) || 0 })}
                        style={{ width: '100%', padding: '0.6rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none', fontSize: '0.95rem', marginTop: '0.5rem' }}
                      />
                    )}
                  </div>

                  {/* Pacing Strategy */}
                  <div>
                    <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Pacing Strategy</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {['even', 'negative', 'positive'].map((s) => (
                        <button key={s}
                          onClick={async () => {
                            const goalTimeSeconds = splitsGoalHours * 3600 + splitsGoalMinutes * 60 + splitsGoalSeconds;
                            setPaceSplits({ ...paceSplits, pacingStrategy: s });
                            if (goalTimeSeconds > 0) {
                              try {
                                const res = await api.put(`/strategy/${raceId}/splits`, { goalTimeSeconds, distanceMiles: paceSplits.distanceMiles, pacingStrategy: s });
                                setPaceSplits(res.data);
                              } catch (err) { console.error('Strategy update failed:', err); }
                            }
                          }}
                          style={{ flex: 1, padding: '0.6rem', background: paceSplits.pacingStrategy === s ? '#FF8A3D' : 'transparent', border: paceSplits.pacingStrategy === s ? 'none' : '1px solid rgba(255, 138, 61, 0.3)', color: paceSplits.pacingStrategy === s ? '#0B0B10' : '#9ca3af', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, textTransform: 'capitalize' }}
                        >{s}</button>
                      ))}
                    </div>
                  </div>

                  {/* Average Pace Display */}
                  {(() => {
                    const goalSec = splitsGoalHours * 3600 + splitsGoalMinutes * 60 + splitsGoalSeconds;
                    if (goalSec > 0 && paceSplits.distanceMiles > 0) {
                      const avgPace = goalSec / paceSplits.distanceMiles;
                      return (
                        <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(255, 138, 61, 0.1)', borderRadius: '8px' }}>
                          <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Avg Pace: </span>
                          <span style={{ color: '#FF8A3D', fontSize: '1.25rem', fontWeight: 700 }}>{formatPace(Math.round(avgPace))}/mi</span>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Save Button */}
                  <button onClick={savePaceSplits}
                    style={{ width: '100%', padding: '0.75rem', background: '#FF8A3D', border: 'none', color: '#0B0B10', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '1rem' }}
                  >Calculate & Save Splits</button>
                </div>
              )}

              {/* Splits Table */}
              {!paceSplits.isTriathlon && paceSplits.splits && paceSplits.splits.length > 0 && (
                <div style={{ marginTop: '1.5rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.2)', borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '0.75rem 1rem', borderBottom: '1px solid rgba(255, 138, 61, 0.15)', color: '#9ca3af', fontSize: '0.85rem', fontWeight: 600 }}>
                    <div>Mile</div><div>Pace</div><div>Cumulative</div>
                  </div>
                  {paceSplits.splits.map((split, idx) => (
                    <div key={split.mile} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '0.6rem 1rem', borderBottom: idx < paceSplits.splits.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none', color: '#FFFFFF', fontSize: '0.95rem', alignItems: 'center' }}>
                      <div style={{ fontWeight: 600 }}>{split.mile}</div>
                      <div>
                        {editingMile === split.mile ? (
                          <input
                            type="text"
                            value={editPaceInput}
                            onChange={(e) => setEditPaceInput(e.target.value)}
                            onBlur={() => {
                              const newPace = parsePaceInput(editPaceInput);
                              if (newPace && newPace > 0) updateMilePace(split.mile, newPace);
                              setEditingMile(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const newPace = parsePaceInput(editPaceInput);
                                if (newPace && newPace > 0) updateMilePace(split.mile, newPace);
                                setEditingMile(null);
                              } else if (e.key === 'Escape') {
                                setEditingMile(null);
                              }
                            }}
                            autoFocus
                            placeholder="M:SS"
                            style={{ width: '65px', padding: '0.3rem 0.5rem', background: 'rgba(255, 138, 61, 0.15)', border: '1px solid #FF8A3D', borderRadius: '6px', color: '#FFFFFF', outline: 'none', textAlign: 'center', fontSize: '0.9rem' }}
                          />
                        ) : (
                          <span
                            onClick={() => { setEditingMile(split.mile); setEditPaceInput(formatPace(split.paceSeconds)); }}
                            style={{ cursor: 'pointer', padding: '0.2rem 0.4rem', borderRadius: '4px', transition: 'background 0.15s' }}
                            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 138, 61, 0.15)'}
                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                          >
                            {formatPace(split.paceSeconds)}/mi
                          </span>
                        )}
                      </div>
                      <div style={{ color: '#9ca3af' }}>{formatTime(split.cumulativeSeconds)}</div>
                    </div>
                  ))}
                  {hasCustomSplits && (
                    <div style={{ padding: '0.6rem 1rem', borderTop: '1px solid rgba(255, 138, 61, 0.15)', textAlign: 'center' }}>
                      <button onClick={resetSplits} style={{ background: 'transparent', border: '1px solid rgba(255, 138, 61, 0.3)', color: '#FF8A3D', padding: '0.4rem 1rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
                        Reset to Calculated
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Checklist Tab */}
        {activeTab === 'checklist' && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ color: '#FFFFFF', margin: 0 }}>Packing Checklist</h3>
              <button
                onClick={fetchPreviousChecklists}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'transparent',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  color: '#FF8A3D',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                Clone from Previous Race
              </button>
            </div>

            <div style={{
              display: 'flex',
              gap: '0.5rem',
              marginBottom: '1.5rem',
              alignItems: 'center'
            }}>
              <select
                value={newItemCategory}
                onChange={(e) => setNewItemCategory(e.target.value)}
                style={{
                  padding: '0.75rem 0.5rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '0.85rem',
                  outline: 'none',
                  minWidth: '100px'
                }}
              >
                {['Custom', 'Gear', 'Admin', 'Body Care', 'Fuel'].map(cat => (
                  <option key={cat} value={cat} style={{ background: '#0B0B10' }}>{cat}</option>
                ))}
              </select>
              <input
                type="text"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addChecklistItem()}
                placeholder="Add item..."
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  outline: 'none'
                }}
              />
              <button
                onClick={addChecklistItem}
                disabled={!newItem.trim()}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: newItem.trim() ? '#FF8A3D' : 'rgba(255, 138, 61, 0.3)',
                  border: 'none',
                  color: '#0B0B10',
                  borderRadius: '8px',
                  cursor: newItem.trim() ? 'pointer' : 'not-allowed',
                  fontWeight: 600
                }}
              >
                Add
              </button>
            </div>

            {checklist.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#9ca3af',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px'
              }}>
                <p>Your checklist is empty. Add items above!</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {getGroupedChecklist().map(({ category, items }) => {
                  const catCompleted = items.filter(i => i.completed).length;
                  const isCollapsed = collapsedCategories.has(category);
                  return (
                    <div key={category}>
                      <button
                        onClick={() => toggleCategory(category)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem 1rem',
                          background: 'rgba(255, 138, 61, 0.1)',
                          border: '1px solid rgba(255, 138, 61, 0.2)',
                          borderRadius: isCollapsed ? '8px' : '8px 8px 0 0',
                          cursor: 'pointer',
                          color: '#FF8A3D',
                          fontWeight: 600,
                          fontSize: '0.9rem'
                        }}
                      >
                        <span>{isCollapsed ? '>' : 'v'} {category}</span>
                        <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.85rem' }}>
                          {catCompleted}/{items.length}
                        </span>
                      </button>
                      {!isCollapsed && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.25rem' }}>
                          {items.map((item) => (
                            <div
                              key={item.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                                padding: '0.75rem 1rem',
                                background: item.completed
                                  ? 'rgba(255, 138, 61, 0.1)'
                                  : 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(255, 138, 61, 0.1)',
                                borderRadius: '6px'
                              }}
                            >
                              <button
                                onClick={() => toggleChecklistItem(item)}
                                style={{
                                  width: '22px',
                                  height: '22px',
                                  borderRadius: '5px',
                                  border: item.completed
                                    ? '2px solid #FF8A3D'
                                    : '2px solid rgba(255, 138, 61, 0.4)',
                                  background: item.completed ? '#FF8A3D' : 'transparent',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#0B0B10',
                                  fontSize: '0.75rem',
                                  flexShrink: 0
                                }}
                              >
                                {item.completed && '✓'}
                              </button>
                              <span style={{
                                flex: 1,
                                color: item.completed ? '#9ca3af' : '#FFFFFF',
                                textDecoration: item.completed ? 'line-through' : 'none',
                                fontSize: '0.95rem'
                              }}>
                                {item.text}
                              </span>
                              <button
                                onClick={() => deleteChecklistItem(item.id)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#6b7280',
                                  cursor: 'pointer',
                                  padding: '0.25rem',
                                  fontSize: '1rem'
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Nutrition Tab */}
        {activeTab === 'nutrition' && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ color: '#FFFFFF', margin: 0 }}>Race Day Nutrition</h3>
              <button
                onClick={() => openNutritionModal()}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#FF8A3D',
                  border: 'none',
                  color: '#0B0B10',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                + Add
              </button>
            </div>

            {nutritionItems.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#9ca3af',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⚡</div>
                <p style={{ marginBottom: '0.5rem' }}>No nutrition plan yet.</p>
                <p style={{ color: '#FF8A3D', fontWeight: 600, cursor: 'pointer' }} onClick={() => openNutritionModal()}>
                  Add your race day fuel
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {nutritionItems.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.2)',
                      borderRadius: '12px',
                      padding: '1.25rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                        <span style={{ color: '#FFFFFF', fontWeight: 600 }}>
                          {item.is_homemade
                            ? (item.homemade_name || 'Homemade')
                            : (item.directory_brand_name || item.brand_name || 'Unknown')}
                        </span>
                        {item.flavor && (
                          <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                            - {item.flavor}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                        {item.timing && (
                          <span style={{
                            padding: '0.2rem 0.6rem',
                            background: 'rgba(255, 138, 61, 0.2)',
                            borderRadius: '12px',
                            color: '#FF8A3D',
                            fontSize: '0.8rem'
                          }}>
                            {item.timing}
                          </span>
                        )}
                        {item.is_homemade && (
                          <span style={{
                            padding: '0.2rem 0.6rem',
                            background: 'rgba(139, 195, 74, 0.2)',
                            borderRadius: '12px',
                            color: '#8bc34a',
                            fontSize: '0.8rem'
                          }}>
                            Homemade
                          </span>
                        )}
                      </div>
                      {item.notes && (
                        <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0', fontSize: '0.85rem' }}>
                          {item.notes}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                      <button
                        onClick={() => openNutritionModal(item)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#FF8A3D',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteNutritionItem(item.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#6b7280',
                          cursor: 'pointer',
                          fontSize: '1.1rem'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Travel Tab */}
        {activeTab === 'travel' && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ color: '#FFFFFF', margin: 0 }}>Travel Details</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setShowPasteModal(true)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'transparent',
                    border: '1px solid rgba(255, 138, 61, 0.3)',
                    color: '#FF8A3D',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  📋 Paste Confirmation
                </button>
                <button
                  onClick={() => openTravelModal()}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#FF8A3D',
                    border: 'none',
                    color: '#0B0B10',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  + Add Manually
                </button>
              </div>
            </div>

            {travelDetails.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#9ca3af',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px'
              }}>
                <p>No travel details yet. Add your flights, hotels, and more!</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {travelDetails.map((travel) => (
                  <div
                    key={travel.id}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.2)',
                      borderRadius: '12px',
                      padding: '1.25rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}
                  >
                    <div style={{ display: 'flex', gap: '1rem', flex: 1 }}>
                      <span style={{ fontSize: '1.5rem' }}>{getTravelIcon(travel.type)}</span>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ color: '#FFFFFF', margin: '0 0 0.25rem 0' }}>
                          {getTravelTitle(travel)}
                        </h4>
                        {getTravelSubtitle(travel) && (
                          <p style={{ color: '#9ca3af', margin: '0 0 0.25rem 0', fontSize: '0.9rem' }}>
                            {getTravelSubtitle(travel)}
                          </p>
                        )}
                        {getTravelDateDisplay(travel) && (
                          <p style={{ color: '#FF8A3D', margin: '0 0 0.25rem 0', fontSize: '0.9rem' }}>
                            {getTravelDateDisplay(travel)}
                          </p>
                        )}
                        {travel.confirmation && (
                          <p style={{ color: '#6b7280', margin: 0, fontSize: '0.85rem' }}>
                            Confirmation: {travel.confirmation}
                          </p>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button
                        onClick={() => openTravelModal(travel)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#6b7280',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          padding: '0.25rem 0.5rem'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteTravelDetail(travel.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#6b7280',
                          cursor: 'pointer',
                          fontSize: '1.25rem'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Race Day Tab */}
        {activeTab === 'raceday' && (
          <div>
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#FFFFFF', margin: 0 }}>Race Day Timeline</h3>
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 138, 61, 0.2)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              {/* Gun Time */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Gun Time
                </label>
                <input
                  type="time"
                  value={timeline.gunTime}
                  onChange={(e) => setTimeline({ ...timeline, gunTime: e.target.value })}
                  style={{ width: '200px', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none', fontSize: '1rem' }}
                />
              </div>

              {/* Suggested Timeline Button */}
              {timeline.events.length === 0 && timeline.gunTime && (
                <button
                  onClick={generateSuggestedTimeline}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'transparent',
                    border: '1px solid rgba(255, 138, 61, 0.3)',
                    color: '#FF8A3D',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    marginBottom: '1rem'
                  }}
                >
                  Start with Suggested Timeline
                </button>
              )}

              {/* Timeline Events */}
              {timeline.events.length > 0 && (
                <div style={{ position: 'relative', paddingLeft: '60px', marginBottom: '1.25rem' }}>
                  {timeline.events.map((event, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: idx < timeline.events.length - 1 ? '0' : '0', position: 'relative' }}>
                      {/* Time */}
                      <div style={{ position: 'absolute', left: '-60px', width: '50px', textAlign: 'right', color: '#9ca3af', fontSize: '0.85rem', fontWeight: 600, paddingTop: '0.6rem' }}>
                        {event.time}
                      </div>
                      {/* Dot + Line */}
                      <div style={{ position: 'relative', width: '20px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: timelineTypeColor(event.type), marginTop: '0.6rem', zIndex: 1 }} />
                        {idx < timeline.events.length - 1 && (
                          <div style={{ width: '2px', background: 'rgba(255, 255, 255, 0.1)', flex: 1, minHeight: '30px' }} />
                        )}
                      </div>
                      {/* Content */}
                      <div style={{ flex: 1, padding: '0.4rem 0 1rem 0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#FFFFFF', fontWeight: 600 }}>{event.label}</span>
                          <button
                            onClick={() => deleteTimelineEvent(idx)}
                            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '1rem', padding: '0.25rem' }}
                          >
                            ×
                          </button>
                        </div>
                        {event.notes && (
                          <div style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: '0.2rem' }}>{event.notes}</div>
                        )}
                        <span style={{
                          display: 'inline-block',
                          marginTop: '0.3rem',
                          fontSize: '0.7rem',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '8px',
                          background: `${timelineTypeColor(event.type)}22`,
                          color: timelineTypeColor(event.type),
                          textTransform: 'capitalize'
                        }}>
                          {event.type}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Event Button */}
              <button
                onClick={() => setShowTimelineEventModal(true)}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: 'transparent',
                  border: '1px dashed rgba(255, 138, 61, 0.3)',
                  color: '#FF8A3D',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  marginBottom: '1.25rem'
                }}
              >
                + Add Event
              </button>

              {/* Notes */}
              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Race Day Notes
                </label>
                <textarea
                  value={timeline.notes}
                  onChange={(e) => setTimeline({ ...timeline, notes: e.target.value })}
                  placeholder="Any additional notes for race day..."
                  rows={3}
                  style={{ width: '100%', padding: '0.75rem 1rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', fontSize: '1rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              {/* Save Button */}
              <button
                onClick={saveTimeline}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  marginTop: '1.25rem',
                  background: '#FF8A3D',
                  border: 'none',
                  color: '#0B0B10',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '1rem'
                }}
              >
                Save Timeline
              </button>
            </div>
          </div>
        )}

        {/* Reflection Tab */}
        {activeTab === 'reflection' && (
          <div>
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#FFFFFF', margin: 0 }}>Race Reflection</h3>
            </div>

            {!isPastRace ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#9ca3af',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📝</div>
                <p style={{ marginBottom: '0.5rem' }}>Available After Race Day</p>
                <p style={{ color: '#FF8A3D', fontWeight: 600, fontSize: '0.9rem' }}>
                  Come back after {race.end_date ? formatDate(race.end_date) : 'your race'} to reflect on your performance
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Planned vs Actual */}
                {(() => {
                  const goalSec = paceSplits.goalTimeSeconds || (timeGoal ? null : 0);
                  const actualSec = reflection.actualTimeSeconds;
                  if (goalSec > 0 && actualSec > 0) {
                    const diff = actualSec - goalSec;
                    const faster = diff < 0;
                    return (
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 138, 61, 0.2)',
                        borderRadius: '12px',
                        padding: '1.25rem'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: '0.75rem' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#9ca3af', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Goal</div>
                            <div style={{ color: '#FFFFFF', fontSize: '1.25rem', fontWeight: 700 }}>{formatTime(goalSec)}</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#9ca3af', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Actual</div>
                            <div style={{ color: '#FFFFFF', fontSize: '1.25rem', fontWeight: 700 }}>{formatTime(actualSec)}</div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', color: faster ? '#4ade80' : '#FF8A3D', fontWeight: 600 }}>
                          {faster ? `${formatTime(Math.abs(diff))} faster than goal` : `${formatTime(Math.abs(diff))} slower than goal`}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Actual Finish Time */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 138, 61, 0.2)',
                  borderRadius: '12px',
                  padding: '1.5rem'
                }}>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Actual Finish Time
                  </label>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <input type="number" min="0" max="23" value={reflectionHours} onChange={(e) => setReflectionHours(parseInt(e.target.value) || 0)} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none', textAlign: 'center', fontSize: '1.1rem' }} />
                      <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.25rem' }}>Hours</div>
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: '1.5rem', fontWeight: 700 }}>:</span>
                    <div style={{ flex: 1 }}>
                      <input type="number" min="0" max="59" value={reflectionMinutes} onChange={(e) => setReflectionMinutes(parseInt(e.target.value) || 0)} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none', textAlign: 'center', fontSize: '1.1rem' }} />
                      <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.25rem' }}>Min</div>
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: '1.5rem', fontWeight: 700 }}>:</span>
                    <div style={{ flex: 1 }}>
                      <input type="number" min="0" max="59" value={reflectionSeconds} onChange={(e) => setReflectionSeconds(parseInt(e.target.value) || 0)} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none', textAlign: 'center', fontSize: '1.1rem' }} />
                      <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.25rem' }}>Sec</div>
                    </div>
                  </div>

                  {/* Star Rating */}
                  <div style={{ marginTop: '1.25rem' }}>
                    <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      Overall Rating
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setReflection({ ...reflection, overallRating: star })}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.75rem',
                            color: star <= (reflection.overallRating || 0) ? '#FF8A3D' : '#6b7280',
                            padding: '0.25rem'
                          }}
                        >
                          {star <= (reflection.overallRating || 0) ? '★' : '☆'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Reflection Text Fields */}
                {[
                  { key: 'whatWorked', label: 'What Worked', placeholder: 'What went well during your race?' },
                  { key: 'whatDidnt', label: "What Didn't Work", placeholder: 'What would you do differently?' },
                  { key: 'fuelNotes', label: 'Fuel Notes', placeholder: 'How was your nutrition and hydration?' },
                  { key: 'weatherImpact', label: 'Weather Impact', placeholder: 'How did conditions affect your race?' },
                  { key: 'wouldChange', label: "What I'd Change", placeholder: 'Changes for next time?' },
                  { key: 'highlight', label: 'Race Highlight', placeholder: 'Best moment of the day' }
                ].map(({ key, label, placeholder }) => (
                  <div key={key}>
                    <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      {label}
                    </label>
                    <textarea
                      value={reflection[key] || ''}
                      onChange={(e) => setReflection({ ...reflection, [key]: e.target.value })}
                      placeholder={placeholder}
                      rows={3}
                      style={{ width: '100%', padding: '0.75rem 1rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', fontSize: '1rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  </div>
                ))}

                {/* Save Button */}
                <button
                  onClick={saveReflection}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#FF8A3D',
                    border: 'none',
                    color: '#0B0B10',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '1rem'
                  }}
                >
                  Save Reflection
                </button>

                {/* Share Your Story CTA */}
                <div style={{
                  background: 'rgba(255, 138, 61, 0.08)',
                  border: '1px solid rgba(255, 138, 61, 0.2)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  textAlign: 'center'
                }}>
                  <p style={{ color: '#FFFFFF', margin: '0 0 0.5rem 0', fontWeight: 600 }}>
                    Want to share your experience with the community?
                  </p>
                  <p style={{ color: '#9ca3af', margin: '0 0 1rem 0', fontSize: '0.9rem' }}>
                    Your story will appear on the race page and in your followers' feeds.
                  </p>
                  <button
                    onClick={() => setActiveTab('stories')}
                    style={{
                      padding: '0.6rem 1.5rem',
                      background: 'transparent',
                      border: '1px solid #FF8A3D',
                      color: '#FF8A3D',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.9rem'
                    }}
                  >
                    Share Your Story
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'stories' && isPastRace && (
          <RaceStories
            raceId={raceId}
            raceName={race.name}
            user={user}
          />
        )}
      </div>

      {/* Save Message Toast */}
      {saveMessage && (
        <div style={{
          position: 'fixed',
          bottom: '2rem',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#4ade80',
          color: '#0B0B10',
          padding: '0.6rem 1.5rem',
          borderRadius: '8px',
          fontWeight: 600,
          fontSize: '0.9rem',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          ✓ {saveMessage}
        </div>
      )}

      {/* Add/Edit Travel Modal */}
      {showTravelModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}
        onClick={() => { setShowTravelModal(false); resetTravelForm(); }}
        >
          <div
            style={{
              background: '#0B0B10',
              border: '1px solid rgba(255, 138, 61, 0.3)',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: '#FFFFFF', marginBottom: '1.5rem' }}>
              {editingTravelItem ? 'Edit Travel Detail' : 'Add Travel Detail'}
            </h3>

            {/* Type Selector */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Type</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[
                  { type: 'flight', icon: '✈️', label: 'Flight' },
                  { type: 'car', icon: '🚗', label: 'Car' },
                  { type: 'stay', icon: '🏨', label: 'Stay' },
                  { type: 'other', icon: '📍', label: 'Other' }
                ].map((t) => (
                  <button
                    key={t.type}
                    onClick={() => setTravelForm({ ...travelForm, type: t.type })}
                    style={{
                      padding: '0.5rem 0.75rem',
                      background: travelForm.type === t.type
                        ? 'rgba(255, 138, 61, 0.3)'
                        : 'rgba(255, 255, 255, 0.05)',
                      border: travelForm.type === t.type
                        ? '2px solid #FF8A3D'
                        : '1px solid rgba(255, 138, 61, 0.2)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: '#FFFFFF',
                      fontSize: '0.85rem',
                      flex: 1
                    }}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Flight Fields */}
            {travelForm.type === 'flight' && (
              <>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Airline *</label>
                    <input type="text" value={travelForm.airline} onChange={(e) => setTravelForm({ ...travelForm, airline: e.target.value })} placeholder="e.g., Delta" style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Flight #</label>
                    <input type="text" value={travelForm.flight_number} onChange={(e) => setTravelForm({ ...travelForm, flight_number: e.target.value })} placeholder="e.g., 1234" style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>From Airport</label>
                    <input type="text" value={travelForm.departure_airport} onChange={(e) => setTravelForm({ ...travelForm, departure_airport: e.target.value })} placeholder="e.g., JFK" style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>To Airport</label>
                    <input type="text" value={travelForm.arrival_airport} onChange={(e) => setTravelForm({ ...travelForm, arrival_airport: e.target.value })} placeholder="e.g., LAX" style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Departure Date</label>
                    <input type="date" value={travelForm.departure_date} onChange={(e) => setTravelForm({ ...travelForm, departure_date: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Departure Time</label>
                    <input type="time" value={travelForm.departure_time} onChange={(e) => setTravelForm({ ...travelForm, departure_time: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Return Date</label>
                    <input type="date" value={travelForm.return_date} onChange={(e) => setTravelForm({ ...travelForm, return_date: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Return Time</label>
                    <input type="time" value={travelForm.return_time} onChange={(e) => setTravelForm({ ...travelForm, return_time: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                </div>
              </>
            )}

            {/* Car Fields */}
            {travelForm.type === 'car' && (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Company *</label>
                  <input type="text" value={travelForm.company} onChange={(e) => setTravelForm({ ...travelForm, company: e.target.value })} placeholder="e.g., Enterprise" style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 2 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Pickup Location</label>
                    <input type="text" value={travelForm.pickup_location} onChange={(e) => setTravelForm({ ...travelForm, pickup_location: e.target.value })} placeholder="e.g., Airport" style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Pickup Date</label>
                    <input type="date" value={travelForm.pickup_date} onChange={(e) => setTravelForm({ ...travelForm, pickup_date: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Time</label>
                    <input type="time" value={travelForm.pickup_time} onChange={(e) => setTravelForm({ ...travelForm, pickup_time: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 2 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Dropoff Location</label>
                    <input type="text" value={travelForm.dropoff_location} onChange={(e) => setTravelForm({ ...travelForm, dropoff_location: e.target.value })} placeholder="e.g., Downtown" style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Dropoff Date</label>
                    <input type="date" value={travelForm.dropoff_date} onChange={(e) => setTravelForm({ ...travelForm, dropoff_date: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Time</label>
                    <input type="time" value={travelForm.dropoff_time} onChange={(e) => setTravelForm({ ...travelForm, dropoff_time: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                </div>
              </>
            )}

            {/* Stay Fields */}
            {travelForm.type === 'stay' && (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Name *</label>
                  <input type="text" value={travelForm.stay_name} onChange={(e) => setTravelForm({ ...travelForm, stay_name: e.target.value })} placeholder="e.g., Hilton Downtown" style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Address</label>
                  <input type="text" value={travelForm.address} onChange={(e) => setTravelForm({ ...travelForm, address: e.target.value })} placeholder="e.g., 123 Main St" style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Check-in Date</label>
                    <input type="date" value={travelForm.checkin_date} onChange={(e) => setTravelForm({ ...travelForm, checkin_date: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Check-out Date</label>
                    <input type="date" value={travelForm.checkout_date} onChange={(e) => setTravelForm({ ...travelForm, checkout_date: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                </div>
              </>
            )}

            {/* Other Fields */}
            {travelForm.type === 'other' && (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Description *</label>
                  <input type="text" value={travelForm.description} onChange={(e) => setTravelForm({ ...travelForm, description: e.target.value })} placeholder="e.g., Amtrak to Boston" style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Start Date</label>
                    <input type="date" value={travelForm.start_date} onChange={(e) => setTravelForm({ ...travelForm, start_date: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Start Time</label>
                    <input type="time" value={travelForm.start_time} onChange={(e) => setTravelForm({ ...travelForm, start_time: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>End Date</label>
                    <input type="date" value={travelForm.end_date} onChange={(e) => setTravelForm({ ...travelForm, end_date: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>End Time</label>
                    <input type="time" value={travelForm.end_time} onChange={(e) => setTravelForm({ ...travelForm, end_time: e.target.value })} style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                  </div>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Notes</label>
                  <input type="text" value={travelForm.notes} onChange={(e) => setTravelForm({ ...travelForm, notes: e.target.value })} placeholder="Optional notes" style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
                </div>
              </>
            )}

            {/* Confirmation # (all types) */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Confirmation #</label>
              <input type="text" value={travelForm.confirmation} onChange={(e) => setTravelForm({ ...travelForm, confirmation: e.target.value })} placeholder="Optional" style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }} />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => { setShowTravelModal(false); resetTravelForm(); }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'transparent',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  color: '#9ca3af',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveTravelDetail}
                disabled={!isTravelFormValid()}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: isTravelFormValid() ? '#FF8A3D' : 'rgba(255, 138, 61, 0.3)',
                  border: 'none',
                  color: '#0B0B10',
                  borderRadius: '8px',
                  cursor: isTravelFormValid() ? 'pointer' : 'not-allowed',
                  fontWeight: 600
                }}
              >
                {editingTravelItem ? 'Save' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Checklist Modal */}
      {showCloneModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}
        onClick={() => setShowCloneModal(false)}
        >
          <div
            style={{
              background: '#0B0B10',
              border: '1px solid rgba(255, 138, 61, 0.3)',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '450px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: '#FFFFFF', marginBottom: '1.5rem' }}>Clone from Previous Race</h3>

            {previousChecklists.length === 0 ? (
              <p style={{ color: '#9ca3af', textAlign: 'center' }}>
                No previous checklists found.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {previousChecklists.map((cl) => (
                  <div
                    key={cl.communityId}
                    onClick={() => cloneChecklist(cl)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.2)',
                      borderRadius: '12px',
                      padding: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    <h4 style={{ color: '#FFFFFF', margin: '0 0 0.5rem 0' }}>
                      {cl.communityName}
                    </h4>
                    <p style={{ color: '#9ca3af', margin: 0, fontSize: '0.85rem' }}>
                      {cl.items.length} items
                    </p>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowCloneModal(false)}
              style={{
                width: '100%',
                marginTop: '1.5rem',
                padding: '0.75rem',
                background: 'transparent',
                border: '1px solid rgba(255, 138, 61, 0.3)',
                color: '#9ca3af',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Nutrition Modal */}
      {showNutritionModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}
        onClick={() => { setShowNutritionModal(false); resetNutritionForm(); }}
        >
          <div
            style={{
              background: '#0B0B10',
              border: '1px solid rgba(255, 138, 61, 0.3)',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: '#FFFFFF', marginBottom: '1.5rem' }}>
              {editingNutritionItem ? 'Edit Nutrition Item' : 'Add Nutrition Item'}
            </h3>

            {/* Homemade toggle */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={nutritionForm.is_homemade}
                  onChange={(e) => setNutritionForm({ ...nutritionForm, is_homemade: e.target.checked })}
                  style={{ accentColor: '#FF8A3D' }}
                />
                <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Homemade recipe</span>
              </label>
            </div>

            {nutritionForm.is_homemade ? (
              <>
                {/* Saved recipes picker */}
                {homemadeRecipes.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                      Saved Recipes
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {homemadeRecipes.map(r => (
                        <button
                          key={r.id}
                          onClick={() => selectRecipe(r)}
                          style={{
                            padding: '0.35rem 0.75rem',
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 138, 61, 0.3)',
                            borderRadius: '16px',
                            color: '#FF8A3D',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          {r.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {homemadeRecipes.length === 0 && !editingNutritionItem && (
                  <button
                    onClick={loadHomemadeRecipes}
                    style={{
                      marginBottom: '1rem',
                      padding: '0.35rem 0.75rem',
                      background: 'transparent',
                      border: '1px solid rgba(255, 138, 61, 0.2)',
                      borderRadius: '16px',
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Load saved recipes
                  </button>
                )}

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Recipe Name *
                  </label>
                  <input
                    type="text"
                    value={nutritionForm.homemade_name}
                    onChange={(e) => setNutritionForm({ ...nutritionForm, homemade_name: e.target.value })}
                    placeholder="e.g., Rice cake balls, Date bites"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      outline: 'none'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Ingredients
                  </label>
                  <textarea
                    value={nutritionForm.homemade_ingredients}
                    onChange={(e) => setNutritionForm({ ...nutritionForm, homemade_ingredients: e.target.value })}
                    placeholder="List ingredients..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Prep Notes
                  </label>
                  <textarea
                    value={nutritionForm.homemade_prep_notes}
                    onChange={(e) => setNutritionForm({ ...nutritionForm, homemade_prep_notes: e.target.value })}
                    placeholder="How to prepare..."
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={nutritionForm.save_as_recipe}
                      onChange={(e) => setNutritionForm({ ...nutritionForm, save_as_recipe: e.target.checked })}
                      style={{ accentColor: '#FF8A3D' }}
                    />
                    <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Save as reusable recipe</span>
                  </label>
                </div>
              </>
            ) : (
              <>
                {/* Brand search */}
                <div style={{ marginBottom: '1rem', position: 'relative' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Brand
                  </label>
                  <input
                    type="text"
                    value={fuelSearch}
                    onChange={(e) => {
                      setFuelSearch(e.target.value);
                      setNutritionForm({ ...nutritionForm, brand_name: e.target.value, brand_id: null });
                      setShowFuelDropdown(true);
                    }}
                    onFocus={() => setShowFuelDropdown(true)}
                    onBlur={() => setTimeout(() => setShowFuelDropdown(false), 200)}
                    placeholder="Search brands (e.g., GU, Maurten...)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      outline: 'none'
                    }}
                  />
                  {showFuelDropdown && (fuelSearch.trim() || filteredBrands.length > 0) && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#1E1E28',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '0 0 8px 8px',
                      maxHeight: '200px',
                      overflow: 'auto',
                      zIndex: 10
                    }}>
                      {filteredBrands.slice(0, 10).map(brand => (
                        <div
                          key={brand.id}
                          onMouseDown={() => {
                            setFuelSearch(brand.name);
                            setNutritionForm({ ...nutritionForm, brand_name: brand.name, brand_id: brand.id });
                            setShowFuelDropdown(false);
                          }}
                          style={{
                            padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                            color: '#FFFFFF',
                            fontSize: '0.9rem',
                            borderBottom: '1px solid rgba(255, 138, 61, 0.1)'
                          }}
                          onMouseEnter={(e) => e.target.style.background = 'rgba(255, 138, 61, 0.15)'}
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                          {brand.name}
                          <span style={{ color: '#6b7280', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                            {brand.type}
                          </span>
                        </div>
                      ))}
                      {fuelSearch.trim() && !fuelBrandDirectory.some(b => b.name.toLowerCase() === fuelSearch.toLowerCase()) && (
                        <div
                          onMouseDown={async () => {
                            try {
                              const res = await api.post('/fuel/directory', { name: fuelSearch.trim() });
                              setFuelBrandDirectory([...fuelBrandDirectory, res.data]);
                              setNutritionForm({ ...nutritionForm, brand_name: res.data.name, brand_id: res.data.id });
                              setShowFuelDropdown(false);
                            } catch (err) {
                              console.error('Error adding brand:', err);
                            }
                          }}
                          style={{
                            padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                            color: '#FF8A3D',
                            fontSize: '0.9rem',
                            fontWeight: 600
                          }}
                          onMouseEnter={(e) => e.target.style.background = 'rgba(255, 138, 61, 0.15)'}
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                          + Add "{fuelSearch.trim()}" as new brand
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Flavor
                  </label>
                  <input
                    type="text"
                    value={nutritionForm.flavor}
                    onChange={(e) => setNutritionForm({ ...nutritionForm, flavor: e.target.value })}
                    placeholder="e.g., Salted Caramel, Lemon Lime"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      outline: 'none'
                    }}
                  />
                </div>
              </>
            )}

            {/* Common fields */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                Timing
              </label>
              <input
                type="text"
                value={nutritionForm.timing}
                onChange={(e) => setNutritionForm({ ...nutritionForm, timing: e.target.value })}
                placeholder="e.g., Mile 6, Before start, Every 45 min"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                Notes
              </label>
              <input
                type="text"
                value={nutritionForm.notes}
                onChange={(e) => setNutritionForm({ ...nutritionForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => { setShowNutritionModal(false); resetNutritionForm(); }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'transparent',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  color: '#9ca3af',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveNutritionItem}
                disabled={nutritionForm.is_homemade ? !nutritionForm.homemade_name.trim() : !fuelSearch.trim()}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: (nutritionForm.is_homemade ? nutritionForm.homemade_name.trim() : fuelSearch.trim())
                    ? '#FF8A3D' : 'rgba(255, 138, 61, 0.3)',
                  border: 'none',
                  color: '#0B0B10',
                  borderRadius: '8px',
                  cursor: (nutritionForm.is_homemade ? nutritionForm.homemade_name.trim() : fuelSearch.trim())
                    ? 'pointer' : 'not-allowed',
                  fontWeight: 600
                }}
              >
                {editingNutritionItem ? 'Save' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paste Confirmation Modal */}
      {showPasteModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}
        onClick={() => setShowPasteModal(false)}
        >
          <div
            style={{
              background: '#0B0B10',
              border: '1px solid rgba(255, 138, 61, 0.3)',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: '#FFFFFF', marginBottom: '0.5rem' }}>Paste Confirmation</h3>
            <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              Paste your booking confirmation email or text below. We'll extract the details automatically.
            </p>

            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder={"Paste your flight, hotel, or car rental confirmation here...\n\nExample:\nDelta Flight 1234\nMarch 15, 2026\nConfirmation: ABC123\nJFK to LAX"}
              rows={8}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 138, 61, 0.3)',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '0.95rem',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
                lineHeight: 1.5
              }}
              autoFocus
            />

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                onClick={() => { setShowPasteModal(false); setPasteText(''); }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'transparent',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  color: '#9ca3af',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleParsePaste}
                disabled={!pasteText.trim()}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: pasteText.trim() ? '#FF8A3D' : 'rgba(255, 138, 61, 0.3)',
                  border: 'none',
                  color: '#0B0B10',
                  borderRadius: '8px',
                  cursor: pasteText.trim() ? 'pointer' : 'not-allowed',
                  fontWeight: 600
                }}
              >
                Extract Details
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Timeline Event Modal */}
      {showTimelineEventModal && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowTimelineEventModal(false)}
        >
          <div
            style={{
              background: '#0B0B10',
              border: '1px solid rgba(255, 138, 61, 0.3)',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '450px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: '#FFFFFF', marginBottom: '1.5rem' }}>Add Event</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Time</label>
                <input
                  type="time"
                  value={timelineEventForm.time}
                  onChange={(e) => setTimelineEventForm({ ...timelineEventForm, time: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Label</label>
                <input
                  type="text"
                  value={timelineEventForm.label}
                  onChange={(e) => setTimelineEventForm({ ...timelineEventForm, label: e.target.value })}
                  placeholder="e.g., Wake up, Breakfast, Warm up"
                  style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Type</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['prep', 'travel', 'race', 'custom'].map((t) => (
                    <button
                      key={t}
                      onClick={() => setTimelineEventForm({ ...timelineEventForm, type: t })}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        background: timelineEventForm.type === t ? timelineTypeColor(t) : 'transparent',
                        border: timelineEventForm.type === t ? 'none' : '1px solid rgba(255, 255, 255, 0.15)',
                        color: timelineEventForm.type === t ? '#0B0B10' : '#9ca3af',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        textTransform: 'capitalize'
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Notes (optional)</label>
                <textarea
                  value={timelineEventForm.notes}
                  onChange={(e) => setTimelineEventForm({ ...timelineEventForm, notes: e.target.value })}
                  placeholder="Any extra details..."
                  rows={2}
                  style={{ width: '100%', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 138, 61, 0.3)', borderRadius: '8px', color: '#FFFFFF', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={() => setShowTimelineEventModal(false)}
                  style={{ flex: 1, padding: '0.75rem', background: 'transparent', border: '1px solid rgba(255, 138, 61, 0.3)', color: '#9ca3af', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={addTimelineEvent}
                  disabled={!timelineEventForm.label.trim() || !timelineEventForm.time}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: timelineEventForm.label.trim() && timelineEventForm.time ? '#FF8A3D' : 'rgba(255, 138, 61, 0.3)',
                    border: 'none',
                    color: '#0B0B10',
                    borderRadius: '8px',
                    cursor: timelineEventForm.label.trim() && timelineEventForm.time ? 'pointer' : 'not-allowed',
                    fontWeight: 600
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyRaceDetail;
